import {
  ATTACH_KINDS,
  buyCategoryOf,
  buyPrice,
  getItemDef,
  linkedDefIds,
  mintPurchase,
  CALIBER_IDS,
  TRADERS,
  WEAPON_CLASSES,
  type AttachKind,
  type ContainerItemDef,
  type ItemDef,
  type ItemInstance,
  type LootItemDef,
  type MedEffect,
  type TraderDef,
  type TraderOffer,
  type WeaponClass,
} from "@rietz/shared";
import { CALIBER_LABELS } from "../inventory/tooltip.js";

/**
 * The market's shelves, sorting and search, with no React in it. The tree is
 * CONTENT-SHAPED: `leafOf` reads a def and answers a leaf, and it is total
 * (the `other` bin exists so it can be), so a new gun lands on the right shelf
 * by its `weaponClass`. The tables that cannot be derived are `Record`s over
 * closed unions or own-property-guarded objects. A row is a TRADER AND A DEF —
 * the same carbine stands on three shelves at three prices, priced by `buyPrice`.
 */

/** One node of the category tree. A leaf is a node with no children. */
export interface MarketNode {
  id: string;
  label: string;
  children: readonly MarketNode[];
}

/**
 * The root — everything in the valley, and the id the market opens on. Not in
 * `MARKET_TREE`; `marketNode` and `pathTo` know about it.
 */
export const MARKET_ROOT_ID = "all";

/**
 * Where a def goes when it fits nothing above. Nothing reaches it today; it
 * exists so `leafOf` is total.
 */
export const MARKET_OTHER_ID = "other";

/** Which weapon shelf each class stands on. A `Record` over the union, so a new
 *  `WeaponClass` is a compile error here rather than a gun in the `other` bin. */
const WEAPON_CLASS_LEAF: Record<WeaponClass, string> = {
  pistol: "weapons.pistols",
  smg: "weapons.smgs",
  shotgun: "weapons.shotguns",
  rifle: "weapons.rifles",
  assault: "weapons.assault",
  marksman: "weapons.marksman",
  sniper: "weapons.snipers",
};

/** What each weapon shelf is called. Plural, because a category is a place with
 *  several things in it. */
const WEAPON_CLASS_LABEL: Record<WeaponClass, string> = {
  pistol: "Pistols",
  smg: "SMGs",
  shotgun: "Shotguns",
  rifle: "Rifles",
  assault: "Assault rifles",
  marksman: "Marksman rifles",
  sniper: "Sniper rifles",
};

/**
 * What each attachment kind is called on the market — a shelf ("Muzzle
 * devices"), not the bench's mounting point ("Muzzle"). A `Record`, so a new
 * kind is a compile error.
 */
const PART_LABEL: Record<AttachKind, string> = {
  optic: "Optics",
  muzzle: "Muzzle devices",
  grip: "Foregrips",
  stock: "Stocks",
  mag: "Magazines",
  barrel: "Barrels",
  handguard: "Handguards",
  pistolgrip: "Pistol grips",
  receiver: "Receivers",
  tactical: "Tactical devices",
};

/**
 * The two hand-split loot categories by def id, own-property guarded like
 * `LOOT_CATEGORY`. A miss falls to the category's `…other`.
 */
const VALUABLE_LEAF: Readonly<Record<string, string>> = {
  taschenuhr: "valuables.trinkets",
  silberkreuz: "valuables.trinkets",
  jagdmesser: "valuables.trinkets",
  gamsbart: "valuables.trophies",
  gamskrickel: "valuables.trophies",
};

const MATERIAL_LEAF: Readonly<Record<string, string>> = {
  naegel: "materials.hardware",
  schraubenzieher: "materials.hardware",
  zuendkerzen: "materials.electrical",
  kupferdraht: "materials.electrical",
  batterie: "materials.electrical",
  benzinkanister: "materials.fire",
  feuerzeug: "materials.fire",
  streichhoelzer: "materials.fire",
};

function leaf(id: string, label: string): MarketNode {
  return { id, label, children: [] };
}

function branch(id: string, label: string, children: readonly MarketNode[]): MarketNode {
  return { id, label, children };
}

/** An own-property lookup into one of the tables above. `null` for a miss,
 *  never a string that came off the prototype. */
function tabled(table: Readonly<Record<string, string>>, defId: string): string | null {
  if (!Object.prototype.hasOwnProperty.call(table, defId)) return null;
  return table[defId] ?? null;
}

/**
 * The category tree in sidebar order: weapons, ammo, parts, gear, medical, the
 * loot bins, the catch-all. Within a category, roster order.
 */
export const MARKET_TREE: readonly MarketNode[] = [
  branch("weapons", "Weapons", [
    ...WEAPON_CLASSES.map((cls) => leaf(WEAPON_CLASS_LEAF[cls], WEAPON_CLASS_LABEL[cls])),
    // Ordnance rides with the weapons, though `buyCategoryOf` prices a grenade
    // as ammo: a player hunting a frag is looking for the thing they throw.
    leaf("weapons.throwables", "Throwables"),
  ]),
  branch(
    "ammo",
    "Ammunition",
    CALIBER_IDS.map((caliber) => leaf(`ammo.${caliber}`, CALIBER_LABELS[caliber])),
  ),
  branch(
    "parts",
    "Weapon parts",
    ATTACH_KINDS.map((kind) => leaf(`parts.${kind}`, PART_LABEL[kind])),
  ),
  branch("gear", "Gear", [
    leaf("gear.vests", "Body armour"),
    leaf("gear.helmets", "Helmets"),
    leaf("gear.rigs", "Tactical rigs"),
    leaf("gear.backpacks", "Backpacks"),
    leaf("gear.cases", "Cases & boxes"),
  ]),
  branch("meds", "Medical", [
    leaf("meds.bleeding", "Bleeding"),
    leaf("meds.fractures", "Fractures"),
    leaf("meds.healing", "Healing"),
  ]),
  branch("provisions", "Provisions", [
    leaf("provisions.food", "Food"),
    leaf("provisions.drink", "Drink"),
    leaf("provisions.other", "Other"),
  ]),
  branch("valuables", "Valuables", [
    leaf("valuables.trinkets", "Trinkets"),
    leaf("valuables.trophies", "Trophies"),
    leaf("valuables.other", "Other"),
  ]),
  branch("materials", "Materials", [
    leaf("materials.hardware", "Hardware"),
    leaf("materials.electrical", "Electrical"),
    leaf("materials.fire", "Fuel & fire"),
    leaf("materials.other", "Other"),
  ]),
  leaf(MARKET_OTHER_ID, "Other"),
];

/** The root as a node, so `marketNode(MARKET_ROOT_ID)` answers like every other
 *  id does and the sidebar can draw its row from the same shape. */
const MARKET_ROOT: MarketNode = {
  id: MARKET_ROOT_ID,
  label: "All",
  children: MARKET_TREE,
};

const NODE_BY_ID: ReadonlyMap<string, MarketNode> = (() => {
  const map = new Map<string, MarketNode>();
  const walk = (node: MarketNode): void => {
    map.set(node.id, node);
    for (const child of node.children) walk(child);
  };
  walk(MARKET_ROOT);
  return map;
})();

/**
 * The one shelf `def` stands on. TOTAL: exhaustive over `ItemDef["kind"]`,
 * every arm an id the tree has.
 */
export function leafOf(def: ItemDef): string {
  switch (def.kind) {
    case "weapon":
      return WEAPON_CLASS_LEAF[def.weaponClass];
    case "grenade":
      return "weapons.throwables";
    case "ammo":
      return `ammo.${def.caliber}`;
    case "attachment":
      return `parts.${def.attachKind}`;
    case "armor":
      return def.equipSlot === "vest" ? "gear.vests" : "gear.helmets";
    case "container":
      return containerLeaf(def.equipSlot);
    case "med":
      return medLeaf(def.effect);
    case "loot":
      return lootLeaf(def);
  }
}

/** A rig, a rucksack or a box — three shelves nobody browses together. */
function containerLeaf(equipSlot: ContainerItemDef["equipSlot"]): string {
  switch (equipSlot) {
    case "rig":
      return "gear.rigs";
    case "backpack":
      return "gear.backpacks";
    case "none":
      return "gear.cases";
  }
}

/**
 * What it MENDS, not what it is called: a healing item that also stops bleeding
 * is filed under healing.
 */
function medLeaf(effect: MedEffect): string {
  switch (effect.t) {
    case "stopBleed":
      return "meds.bleeding";
    case "fixFracture":
      return "meds.fractures";
    case "heal":
      return "meds.healing";
  }
}

/**
 * The `loot` bin refined one level: the category from `buyCategoryOf` (the
 * economy already decided), the shelf inside it from the tables above, except
 * provisions, which derive from `consume`. A `null` category is cash or a
 * quest item → `other`.
 */
function lootLeaf(def: LootItemDef): string {
  switch (buyCategoryOf(def.id)) {
    case "provisions": {
      const consume = def.consume;
      // Cigarettes and matches are provisions nobody eats. They are not food
      // and not drink, and calling them either would be a shelf that lied.
      if (consume === undefined) return "provisions.other";
      // A tie goes to food: only the thing that is mostly water is a drink.
      return consume.hydration > consume.energy ? "provisions.drink" : "provisions.food";
    }
    case "valuables":
      return tabled(VALUABLE_LEAF, def.id) ?? "valuables.other";
    case "materials":
      return tabled(MATERIAL_LEAF, def.id) ?? "materials.other";
    default:
      return MARKET_OTHER_ID;
  }
}

/**
 * The category above `id`, or `null` for the root — read off the id, which IS
 * the path. Split at the FIRST dot, not the last: content ids contain dots
 * (`ammo.7.62x39`'s parent is `ammo`, not `ammo.7`), and the tree is two levels
 * deep by construction.
 */
export function parentOf(id: string): string | null {
  if (id === MARKET_ROOT_ID) return null;
  const dot = id.indexOf(".");
  return dot < 0 ? MARKET_ROOT_ID : id.slice(0, dot);
}

/** The node `id` names, the root included. `null` for anything unknown. */
export function marketNode(id: string): MarketNode | null {
  return NODE_BY_ID.get(id) ?? null;
}

/**
 * Whether `leaf` is `node` or under it — the inheritance rule: picking
 * "Weapons" shows every gun, and the root contains everything.
 */
export function withinNode(leaf: string, node: string): boolean {
  if (node === MARKET_ROOT_ID) return true;
  return leaf === node || leaf.startsWith(`${node}.`);
}

/**
 * The chain from the root down to `id`, root first — what decides which sidebar
 * branch is open, and how `countByNode` charges a row upward.
 */
export function pathTo(id: string): readonly string[] {
  const chain: string[] = [];
  let at: string | null = id;
  while (at !== null) {
    chain.push(at);
    at = parentOf(at);
  }
  return chain.reverse();
}

/** One line of one trader's shelf, priced — the def and the trader together. */
export interface MarketRow {
  /** `${trader.id}:${defId}` — unique across the market, and the tail of the
   *  row's minted iid, so a dialog opened off a row can be resolved back to it. */
  key: string;
  trader: TraderDef;
  offer: TraderOffer;
  def: ItemDef;
  /**
   * One dummy instance per row so the tile and the bench have an iid:
   * `market:<trader>:<def>`, with `#n` per factory part so every bench cell
   * resolves.
   */
  item: ItemInstance;
  /** The tree id this row is filed under — `leafOf(def)`. */
  leaf: string;
  /** What THIS man is asking, `buyPrice(trader, defId)`. */
  price: number;
}

/**
 * Every shelf line of every trader, in `TRADERS` then shelf order. A row whose
 * def is unknown or whose mint fails is skipped, like `offerGrid`.
 */
export function buildMarketRows(traders: readonly TraderDef[]): readonly MarketRow[] {
  const rows: MarketRow[] = [];
  for (const trader of traders) {
    for (const offer of trader.offers) {
      const def = getItemDef(offer.defId);
      if (def === null) continue;
      const key = `${trader.id}:${offer.defId}`;
      let minted = 0;
      const item = mintPurchase(offer.defId, () => {
        const n = minted;
        minted += 1;
        return n === 0 ? `market:${key}` : `market:${key}#${n}`;
      });
      if (item === null) continue;
      rows.push({ key, trader, offer, def, item, leaf: leafOf(def), price: buyPrice(trader, offer.defId) });
    }
  }
  return rows;
}

/**
 * The whole valley's stock, built once at module load — a gun's mint walks its
 * factory tree. Gates are asked per render against the player.
 */
export const MARKET_ROWS: readonly MarketRow[] = buildMarketRows(TRADERS);

/**
 * What the market was asked to narrow to: `item` for one def, `linked` for one
 * hop, `build` for the exact parts a workbench plan is short of. Declared here
 * (without the store's `seq`) so this module stays store-free; the store's type
 * satisfies it structurally.
 */
export type MarketFocusFilter =
  | { t: "item" | "linked"; defId: string }
  | { t: "build"; defIds: readonly string[] };

/**
 * What the screen is showing: a branch, a trader (or all), the search text, and
 * the focus.
 */
export interface MarketFilter {
  /** A tree id, or `MARKET_ROOT_ID` for everything. */
  node: string;
  /** Raw text out of the search box, tokenised here rather than by the caller. */
  query: string;
  /** The sub-tab. `null` is "all traders" — absent, not a trader called "". */
  traderId: string | null;
  /** The right-click that opened the tab, or `null` for the whole market. */
  focus: MarketFocusFilter | null;
}

/** What the focus chip calls itself. The verb only — the SUBJECT is
 *  `focusName`, because the chip draws the two in different colours. */
export function focusLabel(focus: MarketFocusFilter): string {
  if (focus.t === "build") return "Workbench build";
  return focus.t === "item" ? "Filter by item" : "Linked search";
}

/**
 * What a focus is about, named: the def, or — for a build — the shopping list
 * itself. An unknown id falls back to the raw id so the chip stays drawable,
 * and an empty build says so rather than trailing off.
 */
export function focusName(focus: MarketFocusFilter): string {
  if (focus.t !== "build") return getItemDef(focus.defId)?.name ?? focus.defId;
  if (focus.defIds.length === 0) return "nothing";
  return focus.defIds.map((id) => getItemDef(id)?.name ?? id).join(", ");
}

/**
 * Whether `def` answers to `query`. Every token must hit (a second word is a
 * narrowing), on the display name, short name OR def id — the ids carry the
 * short English words a player types ("smg mag" finds `smg_mag_25`). An empty
 * query matches everything.
 */
export function matchesQuery(def: ItemDef, query: string): boolean {
  const tokens = query.toLowerCase().split(/\s+/).filter((t) => t.length > 0);
  if (tokens.length === 0) return true;
  const name = def.name.toLowerCase();
  const short = def.shortName.toLowerCase();
  const id = def.id.toLowerCase();
  return tokens.every((t) => name.includes(t) || short.includes(t) || id.includes(t));
}

/**
 * The rows the screen shows, in build order. The four narrowings are AND-ed —
 * the focus is one of them, not a mode. The `linked` and `build` sets are built
 * once per call, not per row (`linkedDefIds` reads the whole roster).
 */
export function filterRows(rows: readonly MarketRow[], filter: MarketFilter): MarketRow[] {
  const focus = filter.focus;
  const wanted =
    focus === null
      ? null
      : focus.t === "linked"
        ? new Set(linkedDefIds(focus.defId))
        : focus.t === "build"
          ? new Set(focus.defIds)
          : new Set([focus.defId]);
  const focused = (row: MarketRow): boolean => wanted === null || wanted.has(row.def.id);
  return rows.filter(
    (row) =>
      (filter.traderId === null || row.trader.id === filter.traderId) &&
      withinNode(row.leaf, filter.node) &&
      matchesQuery(row.def, filter.query) &&
      focused(row),
  );
}

export type MarketSortKey = "name" | "trader" | "price";

export interface MarketSort {
  key: MarketSortKey;
  dir: 1 | -1;
}

/**
 * What the list opens on: cheapest first — the question a market answers is
 * "what can I afford".
 */
export const DEFAULT_SORT: MarketSort = { key: "price", dir: 1 };

/** Text order without `localeCompare`, so tests do not depend on ICU data. */
function compareText(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * `rows` sorted, as a new array. Ties break by name then trader and are NOT
 * reversed with the direction — a total order, so flipping twice lands where it
 * started.
 */
export function sortRows(rows: readonly MarketRow[], sort: MarketSort): MarketRow[] {
  const sorted = [...rows];
  sorted.sort((a, b) => {
    const primary =
      sort.key === "price"
        ? a.price - b.price
        : sort.key === "trader"
          ? compareText(a.trader.name, b.trader.name)
          : compareText(a.def.name, b.def.name);
    if (primary !== 0) return primary * sort.dir;
    const byName = compareText(a.def.name, b.def.name);
    return byName !== 0 ? byName : compareText(a.trader.name, b.trader.name);
  });
  return sorted;
}

/** Clicking a header: the same column flips, a new column starts ascending. */
export function toggleSort(sort: MarketSort, key: MarketSortKey): MarketSort {
  return sort.key === key ? { key, dir: sort.dir === 1 ? -1 : 1 } : { key, dir: 1 };
}

/**
 * How many rows sit under each node, root included — counted over the rows
 * handed in (already narrowed), so the number answers the current question.
 * One pass via `pathTo`. A node with nothing is absent; read `?? 0`.
 */
export function countByNode(rows: readonly MarketRow[]): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    for (const id of pathTo(row.leaf)) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return counts;
}
